import * as DG from 'datagrok-api/dg';

import {
  NumberingScheme, ChainType, SCHEME_REGIONS,
} from '@datagrok-libraries/bio/src/utils/macromolecule/numbering-schemes';

import {numberSequencesWithImmunum} from './immunum-client';

/** Normalizes an incoming scheme string to the immunum library's accepted values.
 *  The engine only supports 'imgt' and 'kabat'; the dialog's scheme dropdown is
 *  populated from the `choices` option on this function's `scheme` parameter, so
 *  in practice only those two values reach here. */
function toImmunumScheme(scheme: string): 'imgt' | 'kabat' {
  return (scheme || '').toLowerCase() === 'kabat' ? 'kabat' : 'imgt';
}

/** Converts the scheme string to the enum value used by the region-table lookup. */
function toSchemeEnum(scheme: string): NumberingScheme {
  return (scheme || '').toLowerCase() === 'kabat' ? NumberingScheme.Kabat : NumberingScheme.IMGT;
}

/** Immunum returns 'Heavy' / 'Light' but region tables key on Heavy / Light_Kappa
 *  / Light_Lambda. Use chain code to disambiguate: K → Light_Kappa, L → Light_Lambda.
 *  Unknown chains fall back to Heavy so the dialog still gets *some* region set. */
function toRegionChainKey(chainGroup: string, chainCode: string): ChainType {
  if (chainGroup === 'Light')
    return chainCode === 'L' ? ChainType.Light_Lambda : ChainType.Light_Kappa;
  return ChainType.Heavy;
}

/** Builds a row's region-annotation JSON matching the antpack script output:
 *  each entry has id/name/description/start/end/visualType/category/sourceScheme.
 *  The `start` and `end` are **scheme-position codes** (strings), not character
 *  indices — the dialog resolves them to chars via numbering_map. */
function buildRegionAnnotations(
  schemeLabel: string, chainGroup: string, chainCode: string,
): any[] {
  if (!chainGroup) return [];
  const schemeEnum = toSchemeEnum(schemeLabel);
  const regions = SCHEME_REGIONS[schemeEnum];
  const chainKey = toRegionChainKey(chainGroup, chainCode);
  const regionDefs = regions?.[chainKey];
  if (!regionDefs) return [];
  const schemeLower = schemeLabel.toLowerCase();
  return regionDefs.map((r) => ({
    id: `${schemeLower}-${chainGroup}-${r.name}`.toLowerCase(),
    name: r.name,
    description: `${r.name} (${schemeLabel.toUpperCase()} ${r.startPosition}-${r.endPosition})`,
    start: r.startPosition,
    end: r.endPosition,
    visualType: 'region',
    category: 'structure',
    sourceScheme: schemeLabel.toUpperCase(),
    autoGenerated: true,
  }));
}

/** Runs immunum numbering on a sequence column and returns a DataFrame matching
 *  the shape expected by `numbering-ui.ts` (same columns as the antpack Python
 *  script `number_antibody.py`):
 *  - `position_names`      — comma-separated list of numbered position codes
 *  - `chain_type`          — 'Heavy' | 'Light' | ''
 *  - `annotations_json`    — JSON array of FR/CDR region definitions
 *  - `numbering_detail`    — JSON array of {position, aa} per numbered residue
 *  - `numbering_map`       — JSON object mapping position code → char index
 *
 *  The numbering runs in a web worker (see `immunum.worker.ts`) so the main
 *  thread stays responsive even for large columns. */
export async function numberAntibodyColumn(
  seqCol: DG.Column<string>,
  scheme: string,
): Promise<DG.DataFrame> {
  const n = seqCol.length;
  const sequences: string[] = new Array(n);
  for (let i = 0; i < n; i++)
    sequences[i] = seqCol.get(i) ?? '';

  const immunumScheme = toImmunumScheme(scheme);
  const rows = await numberSequencesWithImmunum(sequences, immunumScheme);

  const posCol = DG.Column.fromType(DG.COLUMN_TYPE.STRING, 'position_names', n);
  const chainCol = DG.Column.fromType(DG.COLUMN_TYPE.STRING, 'chain_type', n);
  const annotCol = DG.Column.fromType(DG.COLUMN_TYPE.STRING, 'annotations_json', n);
  const detailCol = DG.Column.fromType(DG.COLUMN_TYPE.STRING, 'numbering_detail', n);
  const mapCol = DG.Column.fromType(DG.COLUMN_TYPE.STRING, 'numbering_map', n);

  for (let i = 0; i < n; i++) {
    const r = rows[i];
    if (!r || r.error || !r.positionNames) {
      posCol.set(i, '');
      chainCol.set(i, '');
      annotCol.set(i, '[]');
      detailCol.set(i, '');
      mapCol.set(i, '');
      continue;
    }
    posCol.set(i, r.positionNames);
    chainCol.set(i, r.chainType);
    annotCol.set(i, JSON.stringify(buildRegionAnnotations(scheme, r.chainType, r.chainCode)));
    detailCol.set(i, JSON.stringify(r.numberingDetail));
    mapCol.set(i, JSON.stringify(r.numberingMap));
  }

  return DG.DataFrame.fromColumns([posCol, chainCol, annotCol, detailCol, mapCol]);
}
